/**
 * Shared appointment hover-history card + hook.
 *
 * Mirrors the day board's hover timeline (GanttCalendar) so the week and month
 * boards show the same lazy-loaded event history (created / rescheduled / status
 * changes / carrier emails) on hover. Events are fetched after a short delay and
 * cached per appointment; a refetch runs each hover so newly-logged events show.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { FileText, ScanLine, Truck } from 'lucide-react';
import { WorkTruckMark, WORK_MARK_SPIN_SECONDS } from '@/components/WorkTruckMark';
/* ⭐ ONE DIRECTION RULE, shared with the calendar's work filter and the tile word
   (§1AV.282). ⛔ Never a local `direction === 'OUTBOUND'` beside it. */
import { workVerb } from '@/lib/labourState';
import { PauseMark, DoneMark } from '@/components/PauseMark';
import { workStatusLabel } from '@/lib/labourState';
import type { AppointmentDto, AppointmentEventDto } from '@/api/types';
import { isDayOnly } from '@/lib/utils';

const HOVER_DELAY_MS = 350;

/**
 * BEING WORKED mark — a truck with a ring of arrows orbiting it, shown on an
 * appointment while loading/unloading is in progress.
 *
 * Same glyph and the same 2.4s rotation as the dock board's work mark, so the
 * thing a scheduler sees on the calendar and the thing a loader sees on the
 * wall TV are recognisably one symbol. It replaced a pulsing emerald dot: the
 * dot said "something is happening here" and nothing more, and it did not
 * survive being read at any distance.
 *
 * White, for the reason DriverBadge is white — appointment tiles are saturated
 * status colors (green/blue/amber/slate/violet) and a tinted glyph disappears
 * on the same hue.
 *
 * The name is kept so every caller (week board, month grid, day gantt) picks
 * the new mark up at once and the two can't drift apart again.
 */
export function LaborPulse() {
    return (
        <WorkTruckMark
            wrapClassName="relative inline-flex h-4 w-4 shrink-0 text-white"
            ringClassName="absolute inset-0 h-full w-full animate-spin motion-reduce:[animation-duration:8s]"
            truckClassName="absolute left-[23%] top-[23%] h-[54%] w-[54%]"
            ringStyle={{ animationDuration: `${WORK_MARK_SPIN_SECONDS}s` }}
            ringStrokeWidth={4.5}
            truckStrokeWidth={2.6}
            title="Being worked — loading/unloading in progress"
        />
    );
}

/**
 * ---- ⭐⭐ THE PAUSED MARK, LaborPulse's TWIN (2026-09-24) -----------------
 *
 * > Pete: *"These do not match now as the truck moving symbol went away. We need
 * > to have the univerasl Pause Symbol on the appointment and ont the labor
 * > panel."*
 *
 * ⛔ `laborActive` IS `State = 'Active'`, so `LaborPulse` correctly vanished the
 * moment somebody paused — and the tile then said NOTHING, which a scheduler
 * reads as "nobody ever started this".
 *   ** ABSENCE IS NOT A STATE. A TILE THAT DRAWS A MARK OR DRAWS NOTHING CAN
 *      ONLY EVER HOLD TWO ANSWERS, AND THE DOMAIN HAS FOUR. **
 *
 * ⭐ SAME SIZE, SAME COLOUR, SAME FOOTPRINT AS ITS TWIN. `PauseMark` shares
 * WorkTruckMark's viewBox and circle radius, so the two swap without moving a
 * pixel of the tile around them — the meaning changes and the layout does not.
 *
 * ⚠️ WHITE, for the reason `LaborPulse` and `DriverBadge` are white: appointment
 * tiles are saturated status colours (green/blue/amber/slate/violet) and a
 * tinted glyph disappears on the same hue.
 *
 * ⚠️ ON BREAK AND PAUSED DRAW THE SAME MARK. A tile has room for one glyph and
 * to somebody scanning the grid both mean "somebody was on this and is not now".
 * The Labor rail, which has room for a word, distinguishes them.
 */
/**
 * ---- ⭐⭐ THE THIRD MARK: THE WORK IS FINISHED (2026-09-24) ----------------
 *
 * > Pete: *"Can we get the unloaded/loaded symbol/text somewhere on the appointment
 * > because it stays arrived but needs a status of that process."* → *"I think the
 * > glyph on the appointment card because its small."*
 *
 * ⛔ THE STATUS IS NOT WRONG. A truck stays ARRIVED until it DEPARTS — that is what
 * the status means, and changing it would break departure. The missing thing was a
 * SECOND fact running alongside it.
 *   ** A STATUS THAT IS RIGHT CAN STILL LEAVE A QUESTION UNANSWERED. THE ANSWER IS
 *      ANOTHER FIELD, NOT A DIFFERENT STATUS. **
 *
 * ⭐ THE TILE GETS THE GLYPH, THE HOVER CARD GETS THE WORD — Pete's own split, and
 * it is the right one: a Gantt bar can be forty pixels wide and a tick fits where
 * "UNLOADED" never would.
 */
export function LaborDoneMark() {
    return (
        <DoneMark
            wrapClassName="relative inline-flex h-4 w-4 shrink-0 text-white"
            markClassName="absolute inset-0 h-full w-full"
            ringStrokeWidth={4.5}
            title="Work finished — the truck has not departed yet"
        />
    );
}

/**
 * ============================================================================
 * ⭐⭐⭐ THE DIAGONAL LOADED / UNLOADED STAMP, ON AN APPOINTMENT TILE
 * ============================================================================
 *
 * > Pete, 2026-09-24: *"if we could put the loaded, unloaded diagnol sign on the
 * > appointments, instead of the checkbox, that would be great!"*
 *
 * ⭐ THE FLOOR BOARD ALREADY TAUGHT THIS SHAPE. `DockBoard.tsx`'s `DoneStamp` puts
 * the same rotated word across a finished door card, from Pete's original ask:
 * *"Can we get a word slash across the screen saying UNLOADED (diagnoloy)... That
 * will let them know for sure the truck is complete with their tasks."* Bringing it
 * to the scheduler costs nothing to teach, which is the whole argument for reusing
 * a learned signal rather than inventing a second one.
 *   ** A NEW SIGNAL THAT REUSES A LEARNED SHAPE COSTS NOTHING TO TEACH. **
 *
 * ⛔⛔ IT IS NOT A SHARED COMPONENT WITH THE BOARD'S, AND THAT IS DELIBERATE.
 * `DockBoard` renders inside `#db-root` with its own stylesheet, its own `--f-stamp`
 * scale and its own palette, sized for a television forty feet away. This one lives
 * on a 36px Gantt lane read at arm's length. Sharing the component would mean one of
 * them wearing the other's sizing.
 *   ** TWO SURFACES CAN SHARE A MEANING AND A SHAPE WITHOUT SHARING A COMPONENT.
 *      WHAT MUST MATCH IS THE WORD AND THE ANGLE, NOT THE PIXELS. **
 * ⚠️ SO THE ANGLE IS COPIED ON PURPOSE: -20deg, the same constant, for the same
 * reason the board records -- a per-tile angle would read as a rendering fault when
 * two chips side by side tilted differently.
 *
 * ---- ⛔⛔ WHY THE TICK DOES NOT GO AWAY ------------------------------------
 *
 * Pete said *"instead of the checkbox"*, and on every tile wide enough that is
 * exactly what happens. But a Gantt block is a fixed 36px lane whose WIDTH is its
 * duration, and "UNLOADED" rotated across a 45-minute chip is not a smaller stamp --
 * it is an illegible smear or a clipped word. Week and Month are worse: their rows
 * are single-line flex strips with no diagonal to draw at all.
 *
 * ⛔ AND THE ANSWER TO "IT DOES NOT FIT" IS NEVER "SHOW NOTHING". A finished load
 * drawing nothing is indistinguishable from one nobody has touched -- the exact fault
 * `PauseMark.tsx` was created for, one surface further on.
 *   ** ABSENCE IS NOT A STATE. A TILE TOO NARROW FOR A STAMP STILL HAS TO SAY THE
 *      WORK IS DONE, AND THE MARK IT REPLACED IS THE THING THAT SAYS IT. **
 * ⭐ So: stamp where it fits, `LaborDoneMark` where it does not. The caller decides,
 * because only the caller knows how wide it is.
 */
export const STAMP_MIN_WIDTH_PX = 95;

/**
 * ⚠️ `absolute inset-0` + `pointer-events-none` -- it covers the whole chip and must
 * never eat a click, a drag handle or a right-click. The Gantt block is a drag
 * source AND a resize target; a stamp that swallowed either would read as a frozen
 * appointment.
 * ⚠️ `overflow-hidden` ON THE WRAPPER, not the text: a rotated word is wider than its
 * box, and without this it escapes over the neighbouring chip.
 * ⚠️ THE TEXT SHADOW IS LOAD-BEARING, not decoration. Tiles are painted in the
 * admin's configurable status palette (migration 135), so the one thing this cannot
 * assume is what colour is behind it. White plus a dark shadow reads on all of them.
 */
export function LoadStateStamp({ direction }: { direction: string }) {
    /* ⛔ THE DIRECTION RULE COMES FROM `workVerb`, NOT FROM A LOCAL TERNARY. It is the
       one place "OUTBOUND is a load, INBOUND is an unload, a BLOCK is neither" is
       written (§1AV.282), and the chip, the tile word and the calendar filter all read
       it. ⚠️ A BLOCK therefore renders NOTHING rather than guessing a verb -- a door
       outage is not loaded or unloaded, and it has no labour to finish. */
    const verb = workVerb(direction);
    if (verb === null) return null;
    return (
        <span className="pointer-events-none absolute inset-0 z-[4] flex items-center justify-center overflow-hidden"
              aria-hidden="true">
            <span className="whitespace-nowrap text-[13px] font-extrabold leading-none tracking-[0.12em] text-white/90"
                  style={{ transform: 'rotate(-20deg)', textShadow: '0 1px 6px rgba(2,10,6,0.85), 0 0 2px rgba(2,10,6,0.9)' }}>
                {verb === 'LOADING' ? 'LOADED' : 'UNLOADED'}
            </span>
        </span>
    );
}

export function LaborPausedMark() {
    return (
        <PauseMark
            wrapClassName="relative inline-flex h-4 w-4 shrink-0 text-white"
            markClassName="absolute inset-0 h-full w-full"
            ringStrokeWidth={4.5}
            title="Paused — a worker is assigned and nobody is on it right now"
        />
    );
}


// Small truck icon shown on an appointment tile once at least one driver is
// on file. Hover shows all drivers' names/phones AND the carrier — read-only
// here; editing happens via right-click "Edit driver…" or the Edit
// Appointment dialog. Silent (renders nothing) when there are no drivers,
// mirroring how LaborPulse only shows while labor is actually active. Shows a
// count badge when there's more than one (multi-truck pickup, or an LTL
// appointment with several drivers — see migration 134).
// Small truck chip shown on an appointment tile once at least one driver is
// on file. Appointment tiles use white text on saturated status-colored
// backgrounds (green/blue/amber/slate/violet — see GanttCalendar STATUS
// classes), so the badge is WHITE on a semi-opaque dark pill: that reads
// legibly on every tile color rather than a tinted glyph that vanishes on
// same-hue tiles. Hover shows all drivers' names/phones AND the carrier.
// Silent (renders nothing) when there are no drivers. Shows a count when >1
// (multi-truck pickup, or an LTL appointment with several drivers — migration 134).
/**
 * ⛔⛔ TWO WAYS A LOAD GETS A DRIVER, AND ONLY ONE OF THEM USED TO SHOW (2026-08-27).
 *
 * Pete: *"When you drag the driver to an appointment and it associates it, the
 * driver icon needs to show on the appointment like it does when you assign it at
 * appointment creation."*
 *
 * ⚠️ ASSIGNING AT CREATION WRITES `dock.AppointmentDriver`, so `appt.drivers` fills
 * and this badge appears. ⛔ ATTACHING AN ARRIVAL DOES NOT — `attachCheckIn` links
 * the CHECK-IN to the appointment and deliberately writes no driver row, which
 * `GanttCalendar` says out loud where it explains `claimedAppointmentIds`. So the
 * badge was answering "was a driver BOOKED", while the scheduler was reading it as
 * "does this load have a driver" — and after a drag those two disagree.
 *
 * ⭐ SO IT NOW ANSWERS THE QUESTION IT LOOKS LIKE IT ANSWERS. A checked-in truck
 * counts. `hasCheckIn` comes from `claimedAppointmentIds`, which the grid already
 * had in hand for the drop rules — no new query, no new column.
 *
 * ⛔ AND IT DOES NOT WRITE AN AppointmentDriver ROW TO FIX THE DISPLAY. That row
 * means "this driver was BOOKED onto this load", `checkInRefusal` reads it to offer
 * the swap-drivers path, and inventing one would make an arrival look like a
 * booking. A rendering gap is not a reason to falsify a record.
 *
 * ⚠️ THE COUNT STILL COMES FROM `drivers` ONLY. A check-in is one truck by
 * definition (one truck = one appointment), so it adds presence, never a number.
 */
export function DriverBadge({ appt, hasCheckIn }: { appt: AppointmentDto; hasCheckIn?: boolean }) {
    const booked = appt.drivers?.length ?? 0;
    if (booked === 0 && !hasCheckIn) return null;
    return (
        <span
            className="inline-flex items-center gap-0.5 shrink-0 rounded-full bg-black/45 px-1 py-0.5 text-white ring-1 ring-white/25"
            aria-label={booked > 0
                ? `${booked} driver(s) assigned`
                : 'a driver is checked in on this appointment'}
        >
            <Truck className="h-3 w-3" aria-hidden="true" />
            {booked > 1 && (
                <span className="text-[10px] font-bold leading-none pr-0.5">{booked}</span>
            )}
        </span>
    );
}

/**
 * ⭐⭐ PAPERWORK AND LICENCE, ON THE TILE (2026-08-27).
 *
 * Pete: *"Lets also add a Paperwork Icon and License Icon to the appointments like
 * we have the Truck icon when a driver is added. Paperwork will be anything
 * attached no matter the number of docs."*
 *
 * ⭐ THE SAME PILL AS `DriverBadge`, DELIBERATELY — white on a semi-opaque dark
 * chip. Appointment tiles are saturated status colours (green/blue/amber/slate/
 * violet), and a tinted glyph vanishes on a same-hue tile. Three marks that mean
 * three different things must at least look like the same KIND of thing.
 *
 * ⛔⛔ IT NOW SHOWS THE COUNT, AND THAT REVERSES WHAT THIS COMMENT SAID THIS
 * MORNING. It read *"NO COUNT, EVER"*, reasoning from Pete's *"anything attached no
 * matter the number of docs"* that four documents instead of three is not a fact a
 * scheduler acts on.
 *
 * ⭐ HE ASKED FOR THE NUMBER A FEW HOURS LATER: *"showing a count where the green
 * dot is on the eyeball of number of docs in the driver card and on the appointment
 * icon would be great."* ⚠️ His first sentence was about what makes the mark APPEAR
 * — any document at all, no threshold — not about what it may say once it is there.
 * The old note read one into the other, and the assertion is reversed rather than
 * quietly dropped.
 *
 * ⚠️ CAPPED AT 9+. Two digits do not fit beside a truck on a chip this size.
 *
 * ⚠️ SILENT WHEN ABSENT, like the truck. The flags arrive from a side-load the
 * server is allowed to fail (see `getPaperworkAndLicenceFlags`), so undefined and
 * false both mean "no mark" — ⛔ and the absence of a mark must never be read as
 * "there is no paperwork". The eye icon on the driver card is what answers that.
 */
export function PaperworkBadge({ appt }: { appt: AppointmentDto }) {
    const n = appt.paperworkCount ?? 0;
    if (n <= 0) return null;
    return (
        <span
            className="inline-flex items-center gap-0.5 shrink-0 rounded-full bg-black/45 px-1 py-0.5 text-white ring-1 ring-white/25"
            aria-label={`${n} document${n === 1 ? '' : 's'} filed on this appointment`}
            title={`${n} document${n === 1 ? '' : 's'} on file`}
        >
            <FileText className="h-3 w-3" aria-hidden="true" />
            <span className="text-[10px] font-bold leading-none pr-0.5">{n > 9 ? '9+' : n}</span>
        </span>
    );
}

/**
 * ⭐ A LICENCE IS ON FILE FOR THIS LOAD — from the gate scan OR from a regular
 * driver who is booked on it and has scanned before. See the SQL for why both.
 *
 * ⛔⛔ THE MARK SAYS ONE IS ON FILE AND NOTHING ELSE. It carries no name, no
 * number and no expiry: `DriverIdentity.RawBarcode` never leaves the database
 * through a viewer, a list or an export, and a tile on a board that faces a
 * lobby is the last place that rule should start bending.
 *
 * ⚠️ THE INTERESTING CASE IS THE ABSENCE, ON AN OUTBOUND. That is where the check
 * matters (design §3) — but the card is where it is called out in amber, because
 * a warning on every un-scanned tile on the board would be most of the board.
 */
export function LicenceBadge({ appt }: { appt: AppointmentDto }) {
    if (!appt.hasLicence) return null;
    return (
        <span
            className="inline-flex items-center shrink-0 rounded-full bg-black/45 px-1 py-0.5 text-white ring-1 ring-white/25"
            aria-label="a driver license is on file for this appointment"
            title="Driver license on file"
        >
            <ScanLine className="h-3 w-3" aria-hidden="true" />
        </span>
    );
}

export function formatEventLabel(ev: AppointmentEventDto, task = 'Work'): { label: string; detail?: string } {
    const nv = (ev.newValue && typeof ev.newValue === 'object') ? ev.newValue : {};
    switch (ev.eventType) {
        case 'Created':     return { label: 'Created' };
        case 'Rescheduled': return { label: 'Rescheduled' };
        case 'WorkStarted':    return { label: `${task} started` };
        case 'WorkerStarted':  return { label: `${nv.worker ?? 'Worker'} started ${task.toLowerCase()}` };
        case 'WorkerFinished': return { label: `${nv.worker ?? 'Worker'} finished`, detail: nv.autoFinished ? 'auto-closed' : undefined };
        /**
         * ---- ⭐⭐ THE PAUSES (2026-09-24) ------------------------------------
         *
         * > Pete: *"we will need this for particular labor data and tracking."*
         *
         * ⛔ THE TIMELINE USED TO SHOW ONLY START AND FINISH, so a truck put down for
         * forty minutes read as one continuous job — while `workedSeconds` on the same
         * assignment already knew better.
         *   ** A SUMMARY AND THE TRAIL BEHIND IT MUST BE DERIVED FROM THE SAME RECORD. **
         *
         * ⚠️ BREAK AND MEAL STAY DISTINCT. Decision 104 gave them separate punch pairs
         * because payroll treats them differently; flattening both to "break" here would
         * put that difference back in the dark on the one screen that exists to show it.
         * ⚠️ `kind` COMES FROM THE SERVER and is only ever 'Break' or 'Meal'; the
         * lower-cased fallback is for a payload written before this shipped.
         */
        case 'WorkerPaused':   return { label: `${nv.worker ?? 'Worker'} paused`, detail: typeof nv.reason === 'string' && nv.reason && nv.reason !== 'Manual' ? String(nv.reason) : undefined };
        case 'WorkerResumed':  return { label: `${nv.worker ?? 'Worker'} resumed` };
        case 'WorkerBreakStarted': return { label: `${nv.worker ?? 'Worker'} started ${String(nv.kind ?? 'Break').toLowerCase()}` };
        case 'WorkerBreakEnded':   return { label: `${nv.worker ?? 'Worker'} back from ${String(nv.kind ?? 'Break').toLowerCase()}` };
        case 'WorkFinished':   return { label: `${task} stopped`, detail: nv.autoFinished ? 'auto-closed at departure' : undefined };
        case 'StatusChanged': {
            const s = nv.status;
            if (s === 'APPROVED') return { label: 'Approved' };
            if (s === 'ARRIVED')  return { label: 'Arrived' };
            if (s === 'DEPARTED') return { label: 'Departed' };
            if (s === 'NOSHOW')   return { label: 'No-show' };
            if (s === 'CANCELLED') return { label: nv.rejected ? 'Rejected' : 'Cancelled', detail: nv.reason || undefined };
            return { label: s ? String(s) : 'Status changed' };
        }
        case 'NotificationSent': {
            const ok = nv.success !== false;
            return { label: ok ? 'Email sent to carrier' : 'Carrier email failed', detail: nv.event ? String(nv.event) : undefined };
        }
        case 'DriverAdded': {
            const detail = nv.driverPhone ? String(nv.driverPhone) : undefined;
            const label = nv.newDriver
                ? `New driver added: ${nv.driverName ?? 'Unknown'}`
                : `Driver added: ${nv.driverName ?? 'Unknown'}`;
            return { label, detail };
        }
        case 'DriverRemoved': {
            return { label: 'Driver removed' };
        }
        /**
         * ⛔ THESE FOUR USED TO FALL THROUGH TO `default`, which prints the raw
         * event type. So a door text where every recipient failed appeared on the
         * timeline as the word "DoorTextSent" — identical to a success, and with
         * the real per-recipient outcomes sitting unread in the payload.
         *
         * ⚠️ That is how a broken SMS path went unnoticed (Pete, 2026-08-20). A log
         * that reports success for a failure is worse than no log: it argues against
         * the person who suspects something is wrong.
         *
         * ⭐ `...TextFailed` is the newer event type; the `success === false` check
         * is what makes an OLD payload written under the misleading name still
         * render honestly, because those rows are already in the database.
         */
        case 'DoorTextSent':
        case 'DoorTextFailed':
        case 'PaperworkTextSent':
        case 'PaperworkTextFailed': {
            const paperwork = ev.eventType.startsWith('Paperwork');
            const what = paperwork ? 'Paperwork text' : 'Dock door text';
            const failed = ev.eventType.endsWith('Failed') || nv.success === false;
            const sent = Array.isArray(nv.sent) ? nv.sent as { phone?: string; outcome?: string; detail?: string | null }[] : [];

            if (!failed) {
                const n = typeof nv.delivered === 'number' ? nv.delivered : sent.length;
                return {
                    label: `${what} sent${n > 1 ? ` · ${n} drivers` : ''}`,
                    // Say WHICH door was texted: "he was told door 12" and "he was
                    // told door 4" are different facts about the same appointment.
                    detail: nv.doorCode ? String(nv.doorCode) : undefined,
                };
            }
            // ⭐ NAME THE REASON. "Failed" sends somebody to the database; the
            // engine's own detail usually says exactly what is wrong.
            const reason = sent.find(x => x.detail)?.detail
                ?? sent.find(x => x.outcome && x.outcome !== 'sent')?.outcome;
            return {
                label: `${what} FAILED`,
                detail: reason ? String(reason) : 'no recipient was reached',
            };
        }
        case 'DoorAssigned':
        case 'DoorMoved': {
            const doorText = (d: any) => (d && (d.code || d.doorId != null)) ? (d.code ?? `#${d.doorId}`) : '—';
            if (ev.eventType === 'DoorMoved') {
                return { label: `Door moved · ${doorText(nv.from)} → ${doorText(nv.to)}` };
            }
            return { label: `Door assigned · ${doorText(nv.to)}` };
        }
        /**
         * ⭐ A5 (2026-09-28) — an order attached by hand, or taken off by a
         * replace / detach. ⛔ Named, not left to `default`: the raw type on the
         * timeline reads as a code, not as something a person did.
         * ⚠️ `replaced` / `movedTo` carry appointment NUMBERS the server wrote, so
         * the line says where the order went without a second lookup.
         */
        case 'WmsOrderAttached': {
            const replaced = Array.isArray(nv.replaced) ? (nv.replaced as unknown[]).map(String) : [];
            return {
                label: `Order attached: ${nv.orderNumber ?? 'unknown'}`,
                detail: replaced.length ? `taken off ${replaced.join(', ')}` : undefined,
            };
        }
        case 'WmsOrderDetached': {
            return {
                label: `Order removed: ${nv.orderNumber ?? 'unknown'}`,
                detail: nv.movedTo ? `moved to ${String(nv.movedTo)}` : undefined,
            };
        }
        default: return { label: ev.eventType };
    }
}

type HoverEvents = AppointmentEventDto[] | 'loading' | { error: string } | null;

export function HoverTimeline({
    appt, x, y, events,
}: {
    appt: AppointmentDto;
    x: number;
    y: number;
    events: HoverEvents;
}) {
    const CARD_W = 300;
    const left = Math.max(8, Math.min(x + 14, window.innerWidth - CARD_W - 12));
    const top = Math.max(8, Math.min(y + 14, window.innerHeight - 340));
    return (
        <div className="fixed z-50 pointer-events-none" style={{ left, top, width: CARD_W }}>
            <div className="rounded-md border bg-card text-foreground shadow-lg p-3 text-xs">
                <div className="font-semibold text-sm leading-tight">{appt.appointmentNumber}</div>
                <div className="text-muted-foreground truncate">
                    {(appt.carrierName ?? appt.carrierCode)} → {(appt.clientName ?? appt.clientCode ?? '—')}
                </div>
                {/**
                  * ---- ⭐⭐ THE WORK STATUS, AT THE TOP (Pete, 2026-09-24) ----------
                  *
                  * > *"a status on the hover over information at the top (loaded,
                  * > Unloaded, Loading Unloading)."*
                  *
                  * ⭐ ABOVE THE FACTS BLOCK, NOT INSIDE IT. Time / Door / Pallets are
                  * things that were AGREED; this is what is HAPPENING. Putting it in
                  * the grid would file a live fact among the bookings.
                  *
                  * ⛔ IT RENDERS NOTHING WHEN NOBODY HAS TOUCHED THE LOAD.
                  * `workStatusLabel` returns null rather than "Not started", because a
                  * truck that has arrived and not been started has no work status —
                  * that is the ABSENCE of this answer, not a fifth value of it.
                  *   ** A VOCABULARY THAT INCLUDES "NOTHING YET" WILL BE PRINTED, AND
                  *      THEN EVERY QUIET ROW CARRIES A LINE SAYING SO. **
                  */}
                {(() => {
                    const ws = workStatusLabel(appt.direction, appt.laborActive, appt.laborPaused, appt.laborDone);
                    if (!ws) return null;
                    /* ⚠️ THE COLOUR MATCHES THE LABOUR RAIL, not the status palette:
                       emerald = somebody is on it, amber = somebody was, muted = done.
                       ** TWO SURFACES SHOWING ONE FACT MUST NOT PICK THEIR OWN
                          PALETTES, OR THE FACT LOOKS LIKE TWO FACTS. ** */
                    const tone = appt.laborActive ? 'text-emerald-600'
                        : appt.laborPaused ? 'text-amber-600'
                        : 'text-muted-foreground';
                    return (
                        <div className={'mt-1 text-[11px] font-semibold uppercase tracking-wide ' + tone}>
                            {ws}
                        </div>
                    );
                })()}
                {/*
                  THE FACTS FIRST, THE HISTORY UNDER THEM.
                  This card used to open straight into an audit trail, so the three
                  things a scheduler hovers to check -- WHEN, WHICH DOOR, WHICH PO --
                  were the three things it didn't say, and finding them meant
                  right-click -> View details. The audit trail is the rarer question;
                  it keeps its place, just below.
                  Everything here is already on the appointment, so this costs no
                  extra fetch and shows instantly, before the history resolves.
                */}
                <div className="mt-2 mb-2 pb-2 border-b grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5">
                    <span className="text-muted-foreground">Time</span>
                    <span className="font-medium tabular-nums">
                        {/* ⭐ "All day" FOR A MASS-CREATED APPOINTMENT (#66). Its
                            stored window spans the facility's operating hours so the
                            capacity and daily-limit queries can see it — but the
                            scheduler agreed to a DAY, not to 05:00, and the first
                            driver told "05:00" because of this line would be entirely
                            justified in turning up at 05:00.
                            ⚠️ Inert until #127 sends schedulingMode: isDayOnly
                            treats absent as TimeSlot, so today this reads as before. */}
                        {isDayOnly(appt) ? 'All day' : (
                            <>
                                {format(parseISO(appt.scheduledStartUtc), 'h:mm')}
                                {'\u2013'}
                                {format(parseISO(appt.scheduledEndUtc), 'h:mm a')}
                            </>
                        )}
                        <span className="text-muted-foreground font-normal">
                            {' · '}{format(parseISO(appt.scheduledStartUtc), 'EEE MMM d')}
                        </span>
                    </span>

                    <span className="text-muted-foreground">Door</span>
                    <span className="font-medium">
                        {appt.dockDoorCode ?? <span className="text-muted-foreground font-normal">Unassigned</span>}
                    </span>

                    {/**
                      * ⭐⭐ PO AND REFERENCE ARE TWO ROWS NOW, NOT ONE (2026-08-27).
                      *
                      * Pete: *"Appointment ID we create in the system really doesn't help
                      * us much on any of the sliders, appointments, etc. Its really the PO
                      * Number, ContainerID, Reference Number, Trailer Number."*
                      *
                      * ⛔ THIS ROW USED TO BE A FALLBACK — `poNumber ?? referenceNumber`,
                      * labelled by whichever won. It was written for Brighton, which
                      * carries a PO and no reference, and on a load carrying BOTH it
                      * silently hid the reference behind the PO. ⚠️ A hidden identifier is
                      * worse than an absent one: the card looks complete, so nobody goes
                      * looking for the number it did not print.
                      *
                      * ⭐ EACH STILL DISAPPEARS WHEN EMPTY. A labelled dash on a card this
                      * small is a row's worth of space saying nothing.
                      */}
                    {appt.poNumber && (
                        <>
                            <span className="text-muted-foreground">PO</span>
                            <span className="font-medium truncate">{appt.poNumber}</span>
                        </>
                    )}

                    {appt.referenceNumber && (
                        <>
                            <span className="text-muted-foreground">Ref</span>
                            <span className="font-medium truncate">{appt.referenceNumber}</span>
                        </>
                    )}

                    {/**
                      * ⭐⭐ THE CONTAINER NUMBER — THE ONE FACT ON THE PAPERWORK (#276).
                      *
                      * Pete, 2026-08-27: *"need to add container number in the schedule
                      * cards… we need to see that to match paperwork."*
                      *
                      * ⛔ IT WENT INTO `formatTitle` FIRST AND NEVER APPEARED. That
                      * builds the chip's NATIVE tooltip, and the chip sets
                      * `title={fetchEvents ? undefined : formatTitle(appt)}` — so on the
                      * real calendar, where `fetchEvents` is always supplied, the native
                      * tooltip is deliberately suppressed in favour of THIS card. The
                      * edit was live, correct, and rendered nowhere.
                      *
                      * ⚠️ NOT FOLDED IN WITH PO/Ref ABOVE. That row shows whichever of
                      * the two exists; a container is a THIRD, different identifier and a
                      * load can carry it alongside a PO. Sharing the row would hide one
                      * behind the other.
                      *
                      * ⭐ MONOSPACED AND NOT TRUNCATED. It is read against a printed
                      * document, and a container number cut short still looks like a
                      * match — which is worse than not showing it at all.
                      */}
                    {appt.containerNumber && (
                        <>
                            <span className="text-muted-foreground">Container</span>
                            <span className="font-mono font-medium tracking-tight [overflow-wrap:anywhere]">
                                {appt.containerNumber}
                            </span>
                        </>
                    )}

                    {appt.palletCount != null && (
                        <>
                            <span className="text-muted-foreground">Pallets</span>
                            <span className="font-medium tabular-nums">{appt.palletCount}</span>
                        </>
                    )}
                </div>
                {appt.drivers && appt.drivers.length > 0 && (
                    <div className="mb-2 pb-2 border-b">
                        <div className="font-medium text-muted-foreground mb-0.5">
                            {appt.drivers.length > 1 ? `Drivers (${appt.drivers.length})` : 'Driver'}
                        </div>
                        <ul className="space-y-0.5">
                            {appt.drivers.map((d, i) => (
                                <li key={i} className="leading-snug">
                                    <span className="font-medium text-foreground">{d.name}</span>
                                    <span className="text-muted-foreground"> · {d.phone}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {events === 'loading' && <div className="text-muted-foreground">Loading history…</div>}
                {events && !Array.isArray(events) && events !== 'loading' && (
                    <div className="text-muted-foreground">Couldn&apos;t load history — {events.error}</div>
                )}
                {Array.isArray(events) && events.length === 0 && <div className="text-muted-foreground">No history yet.</div>}
                {Array.isArray(events) && events.length > 0 && (
                    <ol className="space-y-1.5 max-h-72 overflow-hidden">
                        {events.map((ev) => {
                            const task = appt.direction === 'OUTBOUND' ? 'Loading' : 'Unloading';
                            const f = formatEventLabel(ev, task);
                            return (
                                <li key={ev.auditId} className="flex gap-2">
                                    <span className="text-muted-foreground tabular-nums whitespace-nowrap">
                                        {format(parseISO(ev.eventUtc), 'MMM d, h:mm a')}
                                    </span>
                                    <span className="flex-1 leading-snug">
                                        {f.label}
                                        {f.detail ? <span className="text-muted-foreground"> · {f.detail}</span> : null}
                                    </span>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>
        </div>
    );
}

/**
 * Hover-history behavior for a board. Spread onEnter/onLeave on each chip and
 * render `node` once at the end. `suppressed` disables the card (e.g. mid-drag).
 */
export function useAppointmentHover(
    fetchEvents?: (appointmentId: number) => Promise<AppointmentEventDto[]>,
    suppressed = false,
) {
    const [hover, setHover] = useState<{ appt: AppointmentDto; x: number; y: number } | null>(null);
    const [hoverEvents, setHoverEvents] = useState<HoverEvents>(null);
    const timer = useRef<number | null>(null);
    const hoveredId = useRef<number | null>(null);
    const cache = useRef<Map<number, AppointmentEventDto[]>>(new Map());

    const clear = () => {
        if (timer.current !== null) { window.clearTimeout(timer.current); timer.current = null; }
    };

    const onEnter = useCallback(
        (e: React.MouseEvent, appt: AppointmentDto) => {
            if (!fetchEvents || suppressed) return;
            clear();
            hoveredId.current = appt.appointmentId;
            const x = e.clientX, y = e.clientY;
            timer.current = window.setTimeout(async () => {
                setHover({ appt, x, y });
                const cached = cache.current.get(appt.appointmentId);
                setHoverEvents(cached ?? 'loading');
                try {
                    const evs = await fetchEvents(appt.appointmentId);
                    cache.current.set(appt.appointmentId, evs);
                    if (hoveredId.current === appt.appointmentId) setHoverEvents(evs);
                } catch (err) {
                    if (hoveredId.current === appt.appointmentId && !cached) {
                        setHoverEvents({ error: err instanceof Error ? err.message : 'request failed' });
                    }
                }
            }, HOVER_DELAY_MS);
        },
        [fetchEvents, suppressed],
    );

    const onLeave = useCallback(() => {
        clear();
        hoveredId.current = null;
        setHover(null);
        setHoverEvents(null);
    }, []);

    useEffect(() => () => clear(), []);

    const node = hover ? <HoverTimeline appt={hover.appt} x={hover.x} y={hover.y} events={hoverEvents} /> : null;
    return { onEnter, onLeave, node };
}
